import type { OrderActivityEvent } from '@shared/orderActivity';
import { status as statusTokens } from '@vuelto/tokens';
import type { DeliveryFulfillment, OrderHandoffFields } from '@shared/deliveryTypes';

export type OrderStatus =
  | 'pending'
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'dispatched'
  | 'delivered'
  | 'picked_up'
  | 'cancelled';

export type PickupType = 'pickup' | 'delivery';

export type CustomerOrderRow = {
  id: string;
  status: OrderStatus;
  pickup_type: PickupType;
  delivery_fulfillment: DeliveryFulfillment | null;
  total_amount: number;
  notes: string | null;
  created_at: string;
  updated_at: string | null;
  confirmed_at: string | null;
  ready_at: string | null;
  picked_up_at: string | null;
  customer_id: string;
  customer_name: string;
  customer_phone: string | null;
  branch_id: string;
  organization_id: string;
  contains_restricted: boolean;
  payment_provider: string | null;
  payment_status: string | null;
  pickup_time_slot: string | null;
  order_revision: number;
  resolution_status: 'none' | 'pending_customer' | 'approved' | 'rejected' | 'expired';
  customer_action_deadline: string | null;
  /** Propina Mode A incluida en rider_earning al match. */
  rider_tip_amount?: number;
  delivery_address?: string | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  delivery_fee?: number | null;
  delivery_eta_minutes?: number | null;
  /** Descuentos de promos POS aplicadas online. */
  discount_total?: number;
  cancel_reason?: string | null;
  cancelled_at?: string | null;
  rematch_attempts?: number;
  /** Soft SLA preparing: deadline tras "+15 min". */
  sla_extended_until?: string | null;
  /** Venta POS creada al completar el pedido. */
  sale_id?: string | null;
  /** ETA listo (set al aceptar; fallback 30 min si no hay default_prep_minutes). */
  estimated_ready_at?: string | null;
  /** Código corto de bolsa (siempre). */
  display_code: string;
  /** Rider debe confirmar display_code al retirar. */
  pickup_verification_required: boolean;
  /** Rider debe confirmar PIN en entrega (negocio nunca muestra el PIN). */
  delivery_pin_required: boolean;
};

export type { OrderHandoffFields };

/** Snapshot de opción en `customer_order_items.options` (snake o camel). */
export type CustomerOrderItemOption = {
  choice_name?: string;
  choiceName?: string;
  qty?: number;
};

export type CustomerOrderItemRow = {
  id: string;
  product_id: string;
  product_name: string;
  unit_price: number;
  quantity: number;
  line_total: number;
  item_status: 'active' | 'removed' | 'replaced';
  effective_product_name: string | null;
  effective_unit_price: number | null;
  effective_line_total: number | null;
  unit_price_original?: number | null;
  discount_amount?: number;
  unit_of_measure?: string | null;
  options?: CustomerOrderItemOption[] | null;
  line_note?: string | null;
};

/** Label corto para mods de una línea de pedido online. */
export function formatOrderItemOptionsLabel(
  options: CustomerOrderItemOption[] | null | undefined,
): string {
  if (!options?.length) return '';
  return options
    .map((o) => {
      const name = String(o.choiceName ?? o.choice_name ?? '').trim();
      const qty = Math.max(1, Number(o.qty) || 1);
      if (!name) return '';
      return qty > 1 ? `${name}×${qty}` : name;
    })
    .filter(Boolean)
    .join(', ');
}

/** Código humano de pedido (PV3G). Fallback: prefijo del UUID. */
export function formatOrderDisplayCode(
  displayCode: string | null | undefined,
  fallbackOrderId?: string | null,
): string {
  const fromCode = (displayCode ?? '').trim().replace(/^#/, '');
  if (fromCode) return fromCode.toUpperCase();
  const id = (fallbackOrderId ?? '').trim();
  if (!id) return '';
  return id.replace(/-/g, '').slice(0, 8).toUpperCase();
}

/** Etiqueta canónica de pedido: #PV3G */
export function formatOrderDisplayCodeLabel(
  displayCode: string | null | undefined,
  fallbackOrderId?: string | null,
): string {
  const code = formatOrderDisplayCode(displayCode, fallbackOrderId);
  return code ? `#${code}` : '—';
}

/** Fila de `customer_order_discounts` (breakdown por promo). */
export type CustomerOrderDiscountRow = {
  id: string;
  promotion_id: string | null;
  label: string;
  discount_amount: number;
  scope: 'line' | 'category' | 'cart';
  line_id: string | null;
};

export type CustomerOrderDetail = CustomerOrderRow & {
  order_revision: number;
  resolution_status: 'none' | 'pending_customer' | 'approved' | 'rejected' | 'expired';
  customer_action_deadline: string | null;
  items: CustomerOrderItemRow[];
  discounts: CustomerOrderDiscountRow[];
  changes: CustomerOrderItemChange[];
  events: OrderActivityEvent[];
};

export type CustomerOrderItemChange = {
  id: string;
  order_id: string;
  order_item_id: string;
  change_type: 'remove' | 'replace' | 'reduce_quantity';
  proposed_product_id: string | null;
  proposed_product_name: string | null;
  proposed_unit_price: number | null;
  proposed_quantity: number;
  original_product_name: string;
  original_unit_price: number;
  original_quantity: number;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  expires_at: string;
};

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmado',
  preparing: 'Preparando',
  ready: 'Listo',
  dispatched: 'En camino',
  delivered: 'Entregado',
  picked_up: 'Retirado',
  cancelled: 'Cancelado',
};

export const ORDER_STATUS_COLORS: Record<OrderStatus, string> = {
  pending: statusTokens.pending,
  confirmed: statusTokens.confirmed,
  preparing: statusTokens.preparing,
  ready: statusTokens.ready,
  dispatched: statusTokens.dispatched,
  delivered: statusTokens.delivered,
  picked_up: statusTokens.picked_up,
  cancelled: statusTokens.cancelled,
};

export const ORDER_STATUS_FLOW: OrderStatus[] = [
  'pending',
  'confirmed',
  'preparing',
  'ready',
  'dispatched',
  'delivered',
  'picked_up',
];

/** Stepper steps by fulfillment — pickup skips "En camino"; delivery skips duplicate terminal. */
export function orderStatusSteps(pickupType: PickupType): OrderStatus[] {
  if (pickupType === 'pickup') {
    return ['pending', 'confirmed', 'preparing', 'ready', 'picked_up'];
  }
  return ['pending', 'confirmed', 'preparing', 'ready', 'dispatched', 'delivered'];
}

/**
 * Index for stepper fill/checkmarks.
 * Returns `steps.length` when the order is past the last visible step so every
 * step renders as done (not "current" without a checkmark).
 */
export function orderStepperProgressIndex(
  status: OrderStatus,
  pickupType: PickupType,
): number {
  const steps = orderStatusSteps(pickupType);
  if (pickupType === 'delivery' && (status === 'delivered' || status === 'picked_up')) {
    return steps.length;
  }
  if (pickupType === 'pickup' && status === 'picked_up') {
    return steps.length;
  }
  const idx = steps.indexOf(status);
  return idx < 0 ? 0 : idx;
}

export function canMerchantCancel(status: OrderStatus): boolean {
  return status === 'pending'
    || status === 'confirmed'
    || status === 'preparing'
    || status === 'ready';
}

/** Soft SLA: demorado si preparing y pasó el deadline (sla_extended_until o updated_at+45m). */
export function preparingSlaDeadlineMs(order: {
  status: OrderStatus;
  updated_at: string | null;
  created_at: string;
  sla_extended_until?: string | null;
}): number | null {
  if (order.status !== 'preparing') return null;
  if (order.sla_extended_until) return new Date(order.sla_extended_until).getTime();
  const base = new Date(order.updated_at ?? order.created_at).getTime();
  return base + 45 * 60 * 1000;
}

export function isPreparingDelayed(order: {
  status: OrderStatus;
  updated_at: string | null;
  created_at: string;
  sla_extended_until?: string | null;
}, nowMs = Date.now()): boolean {
  const deadline = preparingSlaDeadlineMs(order);
  return deadline != null && nowMs > deadline;
}

export function canTransitionTo(current: OrderStatus, next: OrderStatus): boolean {
  if (current === 'cancelled') return false;
  if (next === 'cancelled') return canMerchantCancel(current);
  const currentIdx = ORDER_STATUS_FLOW.indexOf(current);
  const nextIdx = ORDER_STATUS_FLOW.indexOf(next);
  return currentIdx >= 0 && nextIdx >= 0 && nextIdx === currentIdx + 1;
}

export function nextActionStatus(
  current: OrderStatus,
  pickupType?: PickupType,
  fulfillment?: DeliveryFulfillment | null,
): OrderStatus | null {
  switch (current) {
    case 'pending':
      return 'confirmed';
    case 'confirmed':
      return 'preparing';
    case 'preparing':
      return 'ready';
    case 'ready':
      if (pickupType !== 'delivery') return 'picked_up';
      // Own delivery: assignment picker owns dispatch (no dual primary CTA).
      if (fulfillment === 'platform_rider') return 'dispatched';
      return null;
    case 'dispatched':
      // Own delivery: employee marks delivered. Platform rider marks via rider app.
      return pickupType === 'delivery' && fulfillment === 'merchant' ? 'delivered' : null;
    case 'delivered':
      return 'picked_up';
    default:
      return null;
  }
}

export function nextActionLabel(
  current: OrderStatus,
  pickupType?: PickupType,
  fulfillment?: DeliveryFulfillment | null,
  payment?: { payment_provider: string | null; payment_status: string | null } | null,
): string | null {
  const unpaidPickup =
    payment != null &&
    payment.payment_provider !== 'mercadopago' &&
    payment.payment_status !== 'paid' &&
    payment.payment_status !== 'refunded';
  switch (current) {
    case 'pending':
      return 'Aceptar';
    case 'confirmed':
      return 'Empezar a preparar';
    case 'preparing':
      return pickupType === 'delivery' ? 'Listo para enviar' : 'Listo para retirar';
    case 'ready':
      if (pickupType !== 'delivery') {
        return unpaidPickup ? 'Cobrar y entregar' : 'Confirmar retiro';
      }
      if (fulfillment === 'platform_rider') return 'Buscar repartidor';
      return null;
    case 'dispatched':
      return fulfillment === 'merchant' ? 'Marcar entregado' : null;
    case 'delivered':
      return unpaidPickup ? 'Cobrar y entregar' : 'Finalizar venta';
    default:
      return null;
  }
}
